// frontend/src/components/dashboard/pages/inventory/services/ingredientsService.js
// Adapted to match your existing API structure and patterns

import apiClient from '../../../../../api/client';

/**
 * Ingredients Service - Complete implementation for ingredients management
 * Matches your existing inventoryService pattern and API structure
 */
const ingredientsService = {
  // =============================================
  // INGREDIENTS MANAGEMENT
  // =============================================

  /**
   * Get all ingredients for the branch
   */
  getIngredients: async () => {
    try {
      console.log('🥬 IngredientsService: Fetching ingredients...');
      const response = await apiClient.get('/api/v1/ingredients');
      console.log('✅ IngredientsService: Ingredients loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error fetching ingredients:', error);
      throw error;
    }
  },

  /**
   * NEW: Create ingredient with package-based pricing
   */
  createIngredient: async (ingredientData) => {
    try {
      console.log('🆕 IngredientsService: Creating ingredient:', ingredientData);
      const response = await apiClient.post('/api/v1/ingredients', ingredientData);
      console.log('✅ IngredientsService: Ingredient created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error creating ingredient:', error);
      throw error;
    }
  },

  /**
   * NEW: Update ingredient
   */
  updateIngredient: async (ingredientId, ingredientData) => {
    try {
      console.log('📝 IngredientsService: Updating ingredient:', ingredientId);
      const response = await apiClient.put(`/api/v1/ingredients/${ingredientId}`, ingredientData);
      console.log('✅ IngredientsService: Ingredient updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error updating ingredient:', error);
      throw error;
    }
  },

  /**
   * NEW: Get low stock alerts
   */
  getIngredientLowStockAlerts: async () => {
    try {
      console.log('⚠️ IngredientsService: Fetching low stock alerts...');
      const response = await apiClient.get('/api/v1/ingredients/alerts');
      console.log('✅ IngredientsService: Alerts loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error fetching alerts:', error);
      throw error;
    }
  },

  /**
   * NEW: Adjust ingredient stock
   */
   adjustIngredientStock: async (ingredientId, adjustmentData) => {
    try {
      console.log('📝 IngredientsService: Adjusting ingredient stock:', ingredientId);
      const response = await apiClient.post(`/api/v1/ingredients/${ingredientId}/adjust`, adjustmentData);
      console.log('✅ IngredientsService: Stock adjusted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error adjusting stock:', error);
      throw error;
    }
  },


  /**
   * Delete ingredient
   */
  deleteIngredient: async (ingredientId) => {
    try {
      console.log('🗑️ IngredientsService: Deleting ingredient:', ingredientId);
      const response = await apiClient.delete(`/api/v1/ingredients/${ingredientId}`);
      console.log('✅ IngredientsService: Ingredient deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error deleting ingredient:', error);
      throw error;
    }
  },


  /**
   * Export ingredients data
   */
  exportIngredients: async () => {
    try {
      console.log('📊 IngredientsService: Exporting ingredients...');
      const response = await apiClient.get('/api/v1/ingredients/export');
      console.log('✅ IngredientsService: Export completed:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error exporting:', error);
      throw error;
    }
  },

  // =============================================
  // INGREDIENT PURCHASES
  // =============================================

  /**
   * Get ingredient purchases
   */
  getIngredientPurchases: async (params = {}) => {
    try {
      console.log('🛒 IngredientsService: Fetching purchases...', params);
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/v1/ingredients/purchases${queryParams ? `?${queryParams}` : ''}`;
      const response = await apiClient.get(url);
      console.log('✅ IngredientsService: Purchases loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error fetching purchases:', error);
      throw error;
    }
  },

  /**
   * Create ingredient purchase
   */
  createIngredientPurchase: async (purchaseData) => {
    try {
      console.log('🛒 IngredientsService: Creating purchase:', purchaseData);
      const response = await apiClient.post('/api/v1/ingredients/purchases', purchaseData);
      console.log('✅ IngredientsService: Purchase created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error creating purchase:', error);
      throw error;
    }
  },

  // =============================================
  // INGREDIENT WASTAGE
  // =============================================

  /**
   * Get ingredient wastage records
   */
  getIngredientWastageRecords: async (params = {}) => {
    try {
      console.log('📋 IngredientsService: Fetching wastage records...', params);
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/v1/ingredients/wastage${queryParams ? `?${queryParams}` : ''}`;
      const response = await apiClient.get(url);
      console.log('✅ IngredientsService: Wastage records loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error fetching wastage records:', error);
      throw error;
    }
  },

  /**
   * Create ingredient wastage record
   */
  createIngredientWastage: async (wastageData) => {
    try {
      console.log('🆕 IngredientsService: Creating wastage record:', wastageData);
      const response = await apiClient.post('/api/v1/ingredients/wastage', wastageData);
      console.log('✅ IngredientsService: Wastage record created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error creating wastage record:', error);
      throw error;
    }
  },

  /**
   * Update ingredient wastage record
   */
  updateIngredientWastage: async (id, wastageData) => {
    try {
      console.log('📝 IngredientsService: Updating wastage record:', id);
      const response = await apiClient.put(`/api/v1/ingredients/wastage/${id}`, wastageData);
      console.log('✅ IngredientsService: Wastage record updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error updating wastage record:', error);
      throw error;
    }
  },

  /**
   * Delete ingredient wastage record
   */
  deleteIngredientWastage: async (id) => {
    try {
      console.log('🗑️ IngredientsService: Deleting wastage record:', id);
      const response = await apiClient.delete(`/api/v1/ingredients/wastage/${id}`);
      console.log('✅ IngredientsService: Wastage record deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ IngredientsService: Error deleting wastage record:', error);
      throw error;
    }
  },

  // =============================================
  // UTILITY METHODS
  // =============================================

  /**
   * Get ingredient stock level for specific ingredient
   */
  getIngredientStock: async (ingredientId) => {
    try {
      console.log('🔍 IngredientsService: Fetching stock for ingredient:', ingredientId);
      const ingredientsResponse = await ingredientsService.getIngredients();
      const ingredientStock = ingredientsResponse.data?.find(ing => ing.ingredient_id === ingredientId);
      
      if (!ingredientStock) {
        return {
          ingredient_id: ingredientId,
          current_stock: 0,
          min_stock: 0,
          max_stock: 1000,
          unit_of_measure: 'kg',
          cost_per_unit: 0,
          stock_status: 'out_of_stock'
        };
      }
      
      return ingredientStock;
    } catch (error) {
      console.error('❌ IngredientsService: Error fetching ingredient stock:', error);
      throw error;
    }
  },

  /**
   * Check if ingredient is in stock
   */
  checkIngredientStock: async (ingredientId, requiredQuantity = 1) => {
    try {
      const ingredientStock = await ingredientsService.getIngredientStock(ingredientId);
      return ingredientStock.current_stock >= requiredQuantity;
    } catch (error) {
      console.error('❌ IngredientsService: Error checking ingredient stock:', error);
      return false; // Assume no stock on error
    }
  },

  /**
   * Validate ingredient data before saving
   */
  validateIngredientData: (ingredientData) => {
    const errors = [];
    
    if (!ingredientData.name?.trim()) {
      errors.push('Ingredient name is required');
    }
    
    if (!ingredientData.unit_of_measure) {
      errors.push('Unit of measure is required');
    }
    
    if (ingredientData.current_stock < 0) {
      errors.push('Current stock cannot be negative');
    }
    
    if (ingredientData.min_stock < 0) {
      errors.push('Min stock cannot be negative');
    }
    
    if (ingredientData.max_stock < ingredientData.min_stock) {
      errors.push('Max stock must be greater than min stock');
    }
    
    if (ingredientData.cost_per_unit < 0) {
      errors.push('Cost per unit cannot be negative');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default ingredientsService;